import React, { useState } from 'react';

export type SortDir = 'asc' | 'desc';
export type SortState = { col: string; dir: SortDir };

/**
 * Hook: local sort state + toggler. Default `dir: 'asc'` on first click,
 * flips on subsequent clicks of the same column.
 *
 * Used by the shared `<SortHeader>` component below.
 */
export function useSortState(
  initial: SortState,
): [SortState, (col: string) => void, React.Dispatch<React.SetStateAction<SortState>>] {
  const [sort, setSort] = useState<SortState>(initial);
  const toggleSort = (col: string) => {
    setSort(prev =>
      prev.col === col
        ? { col, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { col, dir: 'asc' }
    );
  };
  return [sort, toggleSort, setSort];
}

/**
 * Small ▲/▼ arrow shown next to the active sort column. Herda a cor do cabeçalho (10/10/2026): a coluna ordenada é
 * ESTADO, e na Central estado é peso + ícone, não cor. Era laranja.
 */
export const SortArrow: React.FC<{ dir: SortDir }> = ({ dir }) => (
  <span className="ml-1 inline-block">
    {dir === 'asc' ? '▲' : '▼'}
  </span>
);

/**
 * Clickable table header that toggles sort on the given column.
 *
 * Usage:
 * ```tsx
 * const [sort, toggleSort] = useSortState({ col: 'dueDate', dir: 'asc' });
 * <thead>
 *   <tr>
 *     <SortHeader label="Código" col="code" sort={sort} onToggle={toggleSort} className="text-left" />
 *   </tr>
 * </thead>
 * ```
 */
export const SortHeader: React.FC<{
  label: string;
  col: string;
  sort: SortState;
  onToggle: (col: string) => void;
  className?: string;
}> = ({ label, col, sort, onToggle, className = '' }) => (
  <th
    // Ativa: branco e mais pesada (o font-bold vence o font-semibold que alguns chamadores passam); o resto, cinza.
    // O hover é branco, sem a cor de acento.
    className={`p-3 ${sort.col === col ? 'text-white font-bold' : 'text-slate-400 font-medium'} cursor-pointer hover:text-white select-none ${className}`}
    aria-sort={sort.col === col ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
    onClick={() => onToggle(col)}
  >
    {label}
    {sort.col === col && <SortArrow dir={sort.dir} />}
  </th>
);
